// The favorites rules — pure functions, no React (the library.js pattern).
//
// A favorite is Jeff's heart on a track: "keep this one." The board's own
// overlay mechanism (`jeff` on the track row) is the record — the same overlay
// the ingest preserves across Kit's syncs (server.py: Jeff's clicks survive
// re-ingest). So a favorite survives a daily catalog rebake, a container
// redeploy, everything — as long as it rides `payload.jeff`, not a separate
// store that would go stale.
//
// The shape (per track row, inside payload.jeff):
//   favorited: { at: ISO }            — present = favorite, absent = not
//
// This module is the ONE place that knows:
//   • how a track id is read / written into that overlay
//   • how the Favorites playlist is cut (favorited first, newest stamp wins)
// and nothing else imports the overlay shape directly.

// Track id: the catalog's slot_id IS the stable identity (the bake and the
// live board both carry it). The date is NOT part of identity — a favorite on
// "Morning Ignition" means the slot, not one day's render of it.
export const trackKey = (track) =>
  (track && (track.slot_id || track.id)) || null;

export const isFavorited = (track) =>
  Boolean(track && track.jeff && track.jeff.favorited);

// The overlay writer's seam — injectable in tests, defaults to real time.
let _nowFn = () => new Date().toISOString();
export const setNowSource = (fn) => {
  _nowFn = typeof fn === "function" ? fn : () => new Date().toISOString();
};

// Toggle to a target state (or auto-flip when target is undefined).
// Returns { favorited: { at } } — the overlay value to merge into payload.jeff.
// Un-favoriting returns undefined (callers REMOVE the key rather than store
// a falsy { at: null } — overlay truth is presence, not value).
export const favoriteValue = (track, target) => {
  const key = trackKey(track);
  if (!key) return undefined;
  const current = isFavorited(track);
  const next = target === undefined ? !current : Boolean(target);
  if (next) return { favorited: { at: _nowFn() } };
  return undefined;
};

// The catalog item shape (normalizeItems output) carries the overlay flat on
// the track (t.jeff). Board rows carry payload.jeff. Both land here.
export const favoritedTracks = (tracks) =>
  (Array.isArray(tracks) ? tracks : [])
    .filter(isFavorited)
    .slice()
    .sort((a, b) => {
      const at = (t) => (t.jeff.favorited && t.jeff.favorited.at) || "";
      return at(b).localeCompare(at(a)); // most recently favorited first
    });

// The Favorites playlist cut. A favorite is on the SLOT — the song — and the
// multi-day catalog renders that song on many days: the playlist shows ONE row
// per favorited song, the NEWEST render of it (newest day first, then the
// heart stamp as the tie-breaker). The row the heart lives on is still
// addressed by the track's own id, so playing it plays that render.
export const favoritesPlaylist = (tracks) => {
  const sorted = favoritedTracks(tracks);
  const bestBySlot = new Map();
  for (const t of sorted) {
    const key = trackKey(t);
    const prev = bestBySlot.get(key);
    if (!prev || (t.date || "") > (prev.date || "")) {
      bestBySlot.set(key, t);
    }
  }
  const list = Array.from(bestBySlot.values()).sort((a, b) => {
    // keep the heart-stamp order inside a slot group; else newest render first
    const at = (t) => (t.jeff.favorited && t.jeff.favorited.at) || "";
    const byStamp = at(b).localeCompare(at(a));
    if (byStamp !== 0) return byStamp;
    return (b.date || "").localeCompare(a.date || "");
  });
  const total = list.reduce((s, t) => s + (Number(t.duration_s) || 0), 0);
  return {
    key: "favorites",
    label: "Favorites",
    description: "The ones you hearted. Yours — the daily set comes and goes, these stay.",
    headphones: false,
    tracks: list,
  };
};